/**
 * GSAP entrance + exit choreography for the game-start screen.
 *
 * The introduction plays as ONE overlapping cascade, top-to-bottom as the eye reads the page,
 * with no sequential closing tail — the "hybrid" choreography, settled with Andre over four
 * prototype rounds (start-screen-entrance-animations, 2026-08-18; full record:
 * docs/layout/SCREENS.md § "The start screen: one big introduction"). Four groups run
 * concurrently from the entrance's own start (after the boot lead-in, bootLeadIn): the LOGO
 * group, at 0 — the wordmark inscribes at its normal tempo while the six game-type pips etch as
 * a slow left-to-right sweep across the whole introduction, deliberately the last thing still
 * moving (addLogoEtch); the Play button's label word inscribes onto its plate concurrently
 * (addLabelEtch), so brand + call-to-action draw as one opening hand, its gate icon walking in
 * (addGateEtch) once the word lands. The TILES group, one beat in (TILES_GROUP_AT_S) — the six
 * reference-strip tiles enter via the glass-condense gesture
 * (animation/components/glass-condense.js), glass material's own arrival vocabulary, and the
 * top-right menu button condenses in its own pane TRAILING them (MENU_BUTTON_TRAIL_S), so the
 * screen reads as one sweep that ends at the corner (it is glass too — the strip's tiles fly
 * into it on the exit). The CARDS group, one cascade rung later — the two
 * mode cards inscribe their frame then bloom (addPanelEtch). The ROWS group, two rungs later —
 * the Multiplayer bar and the control bank (toggles + card-preference pickers) frame-etch as
 * their own sequential group (addTogglesEtch), parallel with everything else, not a closing
 * beat. The name fades up quietly at 0, off the critical path. The Play button is present and
 * clickable from the first frame throughout (only its word + gate inscribe, no φ-pop). The
 * leaderboard reveal fires once this furniture has settled (`_furnitureSettled`) — the latest
 * of the content, tiles, and rows groups' own settle points — while the pip sweep and the
 * tiles' lingering fog-clear tail are still allowed to run on underneath
 * (revealLeaderboardAfterBeat). A separate promise, `_entranceDone`, resolves only once the
 * whole cascade has genuinely finished (the master's true onComplete) — three other consumers
 * outside this module (game-start-ui.js's idle warmups and its stashed identity-confirmation
 * beat, the initial-load-cls e2e guard) hold on that one instead, unaffected by the
 * leaderboard's earlier gate. When the player
 * commits (Play), `playGameStartExit` fades the name slot out together with the etched logo +
 * cards + rows + button while the strip's tiles fly into the menu button
 * (game-start-strip-flight.js) — a disassembly into the game. GSAP is vendored locally
 * (./vendor/gsap.js) — no CDN, no build step.
 */
import gsap from "../vendor/gsap.js";
import { DISSOLVE_S } from "../app-loader.js";
import { nextFrame } from "../animate.js";
import {
  ETCH_STAMP_DEFAULTS,
  prepareStamp,
  stageStamp,
  addStamp,
  shedStampResidue,
} from "../animation/components/etch-stamp.js";
import {
  addPanelEtch,
  shedPanelEtch,
  PANEL_ETCH_DEFAULTS,
} from "./game-start-panel-etch.js";
import {
  addTogglesEtch,
  shedTogglesEtch,
  EXIT_FADE_SELECTOR,
} from "./game-start-toggles-etch.js";
import {
  addGlassCondense,
  condenseGroupSettleEnd,
} from "../animation/components/glass-condense.js";
import { addStripFlight } from "./game-start-strip-flight.js";
import { STRIP_TILES_SELECTOR } from "./game-start-reference-strip.js";

// play-test: the name reveal's runtime in seconds — its tween's own duration, at natural tempo
// like every group in the cascade (the whole-introduction compression that used to rescale it
// retired with the two-beat choreography). The EXIT reuses it directly as the disassembly's
// runtime (playGameStartExit). Snappy on purpose.
const ENTRANCE_DURATION_S = 0.5;

// play-test: a brief beat after the introduction settles before the leaderboard
// arrives (its frame fade + row cascade), so it reads as its own moment rather
// than tail-gating the last etch stroke. The etch cleanup (shed) stays immediate;
// only the leaderboard reveal waits.
const LEADERBOARD_GAP_S = 0.15;

// play-test: the start screen's quick fade-in when RETURNING to it (Quit). Pairs
// with the board's fade-out (api.js#showGameStart, RETURN_FADE_OUT_S) so the
// board→start swap reads as a smooth transition, not an abrupt one-frame cut. This
// is NOT the etch introduction (SCREENS Decision 5 gates that to first landing) —
// just an opacity smoother over the return.
const RETURN_FADE_IN_S = 0.3;

// The logo pips + wordmark enter via the etch-and-ink Stamp, etch-only: each icon inscribes
// itself (DrawSVG over stroke clones in its own color) and inks in — no announcement press
// (stampScale 1, stampPress false), which would be too grand on 54px logo icons beside the
// bulging wordmark. The six pips are slowed to a contemplative build-up the eye can actually
// follow — deliberately the LAST thing still moving on the screen (Andre's call, four prototype
// rounds, start-screen-entrance-animations, 2026-08-18: the pip etch is the etch he likes most,
// and it barely registered at the entrance's old compressed tempo).
const SLOW_PIP_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // etch at rest size — these icons don't grow then press
  stampPress: false, // etch-only: inscribe, don't stamp
  etchS: 0.9, // play-test: each stroke's draw-on span — contemplative, readable at 1×
  etchStaggerS: 0.08, // play-test: rhythm between an icon's own strokes
  inkS: 0.7, // play-test: the fill bloom, slowed to match
};
// The wordmark etches through the SAME Stamp, etch-only — but its glyphs live in
// a 1000-unit font em (vs the pips' ~40-unit boxes), so the stroke width is in
// font units and the per-letter rhythm is slower (fewer, larger shapes). Tuned
// in the lab's Text Etching tab (Inscribe variant).
const WORDMARK_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // etch at rest size — the wordmark inscribes, never presses
  stampPress: false, // etch-only
  etchS: 0.28, // play-test: each letter's outline draw-on span
  etchStaggerS: 0.05, // play-test: gap between letters — the inscription rhythm
  inkS: 0.28, // play-test: the fill bloom
  etchWidth: 11, // why: font units (1000upm em) — a hairline trace of the letter contour
};
const PIP_ICON_SELECTOR = ".game-start-logo-pip-group svg";
const WORDMARK_SELECTOR = ".game-start-logo-text";

// The Play button's enter-gate inscribes as the "now enter" mark right after the
// label word lands. Stroke-native (the gate is a stroke icon — walls in a subtle
// <g>, the full-strength arrow), and etch-only like the logo pips so it speaks the
// same calm inscription idiom rather than an announcement press. The button is
// static (no φ-pop), so the gate always draws onto a settled target.
const GATE_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  strokeNative: true, // the gate draws its own strokes — no clones, no ink group
  stampScale: 1, // etch at rest size — no announcement scale on the icon
  stampPress: false, // etch-only: the press stays on the button φ-pop
  etchS: 0.3, // play-test: each stroke's draw-on span — legible at button size
  etchStaggerS: 0.08, // play-test: walls draw first, the arrow enters after — "build the gate, then step through"
};
const GATE_ICON_SELECTOR = ".play-button-icon";

// The Play button's label word ("Spielen"/"Play") inscribes onto the button from
// the start of the entrance — the word engraves itself onto the gold plate (which
// is present + clickable throughout), then the gate icon walks in after it. Its
// glyphs live in the 1000-unit font em (like the wordmark), so the stroke width is
// in font units and the per-letter rhythm matches an inscription. Etch-only.
const LABEL_ETCH_CONFIG = {
  ...ETCH_STAMP_DEFAULTS,
  stampScale: 1, // etch at rest size — the word inscribes, never presses
  stampPress: false, // etch-only
  etchS: 0.28, // play-test: each letter's outline draw-on span
  etchStaggerS: 0.05, // play-test: gap between letters — the inscription rhythm
  inkS: 0.28, // play-test: the fill bloom
  etchWidth: 11, // why: font units (1000upm em) — a hairline trace of the contour
};
const PLAY_LABEL_SELECTOR = ".play-button-label-text";
// ── The four-group cascade ────────────────────────────────────────────────────
// play-test: a beat of empty stage between the loader fully dissolving and the
// introduction starting, so the eye registers "loader gone" before "etch begins"
// — the two read as separate events, not a crossfade. Only applied on the fresh
// boot (overlay present); a return starts at 0. See bootLeadIn.
const BLANK_GAP_S = 0.02;
// play-test: the beat between the label word finishing its inscription and the
// gate beginning its own. The gate's start is DERIVED from the label etch's
// measured end; 0 starts it the instant the last letter settles.
const GATE_ETCH_GAP_S = 0;
// play-test: the top-to-bottom cascade's two knobs (the "hybrid" choreography, settled with
// Andre over four prototype rounds, 2026-08-18). TILES_GROUP_AT_S is when the tiles group
// departs, measured from the entrance's own start (after leadIn); GROUP_STAGGER_S is the
// constant beat between each later group's own departure — tiles → cards → rows, one rung
// each. The logo group runs from 0 (its own slow sweep, addLogoEtch), so four groups overlap
// from here with no sequential closing tail anywhere, unlike the old two-beat + compression
// story this replaced, whose own knobs went with it.
const TILES_GROUP_AT_S = 0.35;
const GROUP_STAGGER_S = 0.13;
const CARDS_GROUP_AT_S = TILES_GROUP_AT_S + GROUP_STAGGER_S; // why: one cascade rung after the tiles
const ROWS_GROUP_AT_S = TILES_GROUP_AT_S + 2 * GROUP_STAGGER_S; // why: two cascade rungs after the tiles
// play-test: how far the menu button's pane trails the tiles beat — the strip's sweep runs its
// six tiles left to right, then the corner condenses after them (Andre's live call,
// glass-condense-adoption: the menu is the last thing that should draw the eye, never
// simultaneous with the strip). 1.0 starts it just as the sixth tile's pane finishes forming
// (5·staggerS + paneInS at defaults), so the sweep hands off rather than pausing dead.
const MENU_BUTTON_TRAIL_S = 1.0;

// The logo's two etch "kinds", as data: the Stamp config and the host class (ephemeral
// animation DOM, shed at the end) — the pips read slowly, the wordmark holds its own tempo.
const ETCH_KINDS = {
  pip: {
    config: SLOW_PIP_ETCH_CONFIG,
    className: "game-start-logo-pip-host",
  },
  wordmark: {
    config: WORDMARK_ETCH_CONFIG,
    className: "game-start-logo-text-host",
  },
};

// Wrap one logo element in a host span the Stamp can scale/own (prepareStamp
// drives the host's `> svg`). The span is ephemeral animation DOM — shed +
// unwrapped at the entrance's completion, so the resting screen is exactly the
// factory SVGs. The class distinguishes pip hosts from the wordmark host.
function wrapEtchHost(svg, className) {
  const host = document.createElement("span");
  host.className = className;
  host.style.display = "inline-flex";
  host.style.transformOrigin = "center";
  svg.replaceWith(host);
  host.appendChild(svg);
  return host;
}

function unwrapEtchHost(host) {
  const svg = host.querySelector(":scope > svg");
  if (svg != null) host.replaceWith(svg);
}

// The logo's etch units in spatial order: left pip group, the wordmark, right pip
// group — each an {svg, ...ETCH_KINDS kind} addLogoEtch weaves in turn.
function logoEtchUnits(screen) {
  const groups = [...screen.querySelectorAll(".game-start-logo-pip-group")];
  const wordmark = screen.querySelector(WORDMARK_SELECTOR);
  const pips = (group) => (group ? [...group.querySelectorAll("svg")] : []);
  const right = groups.length > 1 ? groups[groups.length - 1] : null;
  return [
    ...pips(groups[0]).map((svg) => ({ svg, ...ETCH_KINDS.pip })),
    ...(wordmark ? [{ svg: wordmark, ...ETCH_KINDS.wordmark }] : []),
    ...pips(right).map((svg) => ({ svg, ...ETCH_KINDS.pip })),
  ];
}

// play-test: gap between pip starts — the slow sweep's travel. The wordmark keeps its own
// tempo and anchors the sweep's start, so the brand name lands early and the pips become its
// slow surround.
const SLOW_PIP_STAGGER_S = 0.5;

// Weave the whole-logo inscription onto the master at `at` (concurrent with the rest of the
// cascade — the logo group runs from the entrance's own 0): walk the etch units left-to-right,
// each wrapping in a host, staging, and adding its own etch-only Stamp. The wordmark starts at
// `at` directly and anchors the sweep; each pip departs on its own slow cursor after it, so the
// pips are deliberately the last thing still moving (SLOW_PIP_ETCH_CONFIG's header). Returns
// the {host, stamp} pairs for the shed.
function addLogoEtch(master, screen, at) {
  const stamps = [];
  let pipCursor = 0;
  for (const { svg, config, className } of logoEtchUnits(screen)) {
    const isWordmark = className === ETCH_KINDS.wordmark.className;
    const host = wrapEtchHost(svg, className);
    // prepareStamp returns null only when the host has no `> svg` child, but
    // wrapEtchHost just made `svg` exactly that child — so it is always non-null
    // here. Recording every wrapped host unconditionally keeps the shed total:
    // no host can be wrapped without landing in `stamps` to be unwrapped, so the
    // screen always rests as clean factory SVGs.
    const stamp = prepareStamp(host, config);
    stageStamp(host, stamp, config, { opacity: 1 });
    const sub = gsap.timeline();
    addStamp(sub, host, stamp, config, { opacity: 1 });
    master.add(sub, isWordmark ? at : at + pipCursor);
    if (!isWordmark) pipCursor += SLOW_PIP_STAGGER_S;
    stamps.push({ host, stamp });
  }
  return { stamps };
}

// On the fresh-app boot the loading overlay is mid-dissolve as the entrance fires
// (both start the same frame — see app-loader.js / game.js boot order). Lead the
// introduction in until the overlay has fully dissolved (DISSOLVE_S) plus a brief
// blank beat (BLANK_GAP_S), so the loader clears to an empty stage and the etch
// then begins as its own event — not under a still-fading bar. A return to the
// start screen has no overlay, so it begins at 0.
function bootLeadIn() {
  return document.getElementById("app-loader") != null
    ? DISSOLVE_S + BLANK_GAP_S
    : 0;
}

// At the entrance's end, return each etched element to factory presentation: shed
// the Stamp's ink/etch residue, then unwrap the host so the screen rests as plain
// SVGs (the stylesheet's resting state, no GSAP transforms).
function shedEntranceEtch(stamps) {
  for (const { host, stamp } of stamps) {
    shedStampResidue(host, stamp);
    unwrapEtchHost(host);
  }
}

// Weave the Play button's label word onto the master from the start of the
// entrance. Wraps the glyph SVG in an etch host (shed back to the factory SVG at
// the end), stages it hidden, and adds its etch-only Stamp at `startAt`. Returns
// the {host, stamp, end} pair plus the measured finish (the gate etch starts after
// the word lands), or null when the label is the text fallback (no glyph SVG — a
// language without extracted glyphs), in which case the gate follows from the start.
function addLabelEtch(master, screen, startAt) {
  const label = screen.querySelector(PLAY_LABEL_SELECTOR);
  if (label == null) return null;
  const host = wrapEtchHost(label, "play-button-label-host");
  const stamp = prepareStamp(host, LABEL_ETCH_CONFIG);
  stageStamp(host, stamp, LABEL_ETCH_CONFIG, { opacity: 1 });
  const sub = gsap.timeline();
  addStamp(sub, host, stamp, LABEL_ETCH_CONFIG, { opacity: 1 });
  master.add(sub, startAt);
  return { host, stamp, end: startAt + sub.duration() };
}

// Weave the Play button's gate-icon inscription onto the master as the "now enter"
// mark after the label word lands. Wraps the icon in an etch host (shed back to the
// factory SVG at the end, like the logo pips), stages it hidden, and adds its etch-
// only Stamp at `startAt`. Returns the {host, stamp} pair for the shed. The play
// button always carries its gate icon (buildPlayButton), so the query is trusted
// non-null, the way addLogoEtch trusts its wrapped hosts.
function addGateEtch(master, screen, startAt) {
  const icon = screen.querySelector(GATE_ICON_SELECTOR);
  const host = wrapEtchHost(icon, "play-button-icon-host");
  const stamp = prepareStamp(host, GATE_ETCH_CONFIG);
  stageStamp(host, stamp, GATE_ETCH_CONFIG, { opacity: 1 });
  const sub = gsap.timeline();
  addStamp(sub, host, stamp, GATE_ETCH_CONFIG, { opacity: 1 });
  master.add(sub, startAt);
  return { host, stamp };
}

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// The etch is an INTRODUCTION: it plays once, on the fresh app landing, and is
// not replayed when the player returns to the start screen after quitting a game
// (SCREENS Decision 5 — re-introducing on every return would cheapen the
// gesture, the same reasoning the panel-etch applies to mode toggles). A page
// load re-initialises this module, so this flag is false only on the page's
// first render — exactly what distinguishes a true landing from an in-page return
// (renderGameStart rebuilds the screen each time, but module state survives).
// The exit (playGameStartExit) is unaffected: leaving is a real transition.
let introHasPlayed = false;

// Reset the introduction latch so each test starts from a fresh-landing state.
export function _resetForTest() {
  introHasPlayed = false;
}

// ── The chunked build (tbt-reshaping slice 1 §2) ─────────────────────────────
// The introduction used to build in one ~1.6s synchronous task (this section's
// four functions, all called back-to-back from assembleEntranceMaster). It's now
// split at its natural seams across awaited frame yields, so boot's main thread
// never blocks that long — see assembleEntranceMaster below for the five-chunk
// orchestration. Each function here is one chunk's own build step, still built
// onto the SAME `content` sub-timeline threaded through (label/gate/cards/name) —
// only WHEN each piece is added changed, not what gets built or at which
// position/duration. The logo, tiles, and rows groups are NOT part of `content` —
// they're woven straight onto `master` at their own cascade positions (see the
// header comment above).

// Chunk (a): the whole-logo inscription (left pips, wordmark, right pips) — the
// entrance's own LOGO group, at 0 — the build's first task. Nothing precedes it,
// so it's the one chunk with no abort guard (a Play click can't race a
// continuation that doesn't exist yet).
function buildLogoChunk(master, screen, leadIn) {
  return addLogoEtch(master, screen, leadIn).stamps;
}

// Chunk (b): the Play button's label word, then its gate icon once the word lands.
function buildLabelGateChunk(content, screen) {
  const label = addLabelEtch(content, screen, 0);
  const gate = addGateEtch(
    content,
    screen,
    (label?.end ?? 0) + GATE_ETCH_GAP_S,
  );
  return { label, gate };
}

// Chunk (c): the mode cards' panel etch — the cascade's CARDS group, one rung after the tiles
// group (CARDS_GROUP_AT_S) — then the name reveal. Both woven onto `content`, which plays at its
// own natural tempo (no whole-introduction compression anymore — every group in the cascade
// keeps its own real-seconds pace).
function buildPanelNameChunk(content, screen) {
  const panel = addPanelEtch(
    screen.querySelector(".mode-cards"),
    "continuous",
    PANEL_ETCH_DEFAULTS,
  );
  content.add(panel.tl, CARDS_GROUP_AT_S);
  // The name slot rises + fades in calmly at 0: it sits just under the logo, so it arrives early
  // in the top-to-bottom reading order, and it is off the critical path (no overshoot, no pop —
  // the one cascade participant that neither etches nor condenses, SCREENS Decision 2).
  content.from(
    screen.querySelectorAll(".game-start-name-slot"),
    {
      autoAlpha: 0,
      y: 14,
      duration: ENTRANCE_DURATION_S,
      ease: "power3.out",
    },
    0,
  );
  return panel;
}

// Chunk (d): content is complete — only NOW does it join the master (content.play() first: it
// was built paused so real time elapsing across chunks (a)-(c) couldn't advance its own
// playhead while it was still a standalone, root-ticked timeline — see playGameStartEntrance's
// header), so the master's duration and onComplete bookkeeping never see a half-built child.
// Then the TILES and ROWS groups (the cascade's remaining two groups —
// animation/components/glass-condense.js / game-start-toggles-etch.js), the Play-plate hold, and
// the completion wiring — what assembleEntranceMaster used to do in one synchronous tail after
// the build.
function attachContentAndWireCompletion(
  master,
  screen,
  content,
  { leadIn, leaderboard, stamps, panel, entranceDone, furnitureSettled },
) {
  content.play();
  master.add(content, leadIn);
  const tilesAt = leadIn + TILES_GROUP_AT_S;
  const { tiles, menuPane } = addTilesBeatGlass(master, screen, tilesAt);
  const rowsAt = leadIn + ROWS_GROUP_AT_S;
  const rows = addTogglesEtch(screen);
  master.add(rows.tl, rowsAt);
  holdPlayPlateThroughLoad(master, screen, leadIn);

  // The leaderboard-gate re-key: fire once every group but the slow pip sweep and the tiles'
  // own lingering fog-clear tail has visually settled — the furniture, not the last stroke
  // (SCREENS "furniture settled" / this file's own header). Only condenseGroupSettleEnd is a
  // mirror of its module's per-item math, and it has to be: glass-condense's settle deliberately
  // EXCLUDES the fog-clear tail, so settle ≠ end there by design. The rows group's settle IS its
  // end (the last item's frame-fade is its last tween), so its term is simply the timeline's own
  // duration — nothing to drift. This resolves `_furnitureSettled` (via
  // revealLeaderboardAfterBeat, below), a promise scoped to the leaderboard alone —
  // `_entranceDone`'s own, unchanged true-completion contract (waited on by game-start-ui.js's
  // idle warmups and identity-confirmation beat and the initial-load-cls e2e guard) still
  // resolves from the real `onComplete` beneath.
  const furnitureSettledAt = Math.max(
    leadIn + content.duration(),
    condenseGroupSettleEnd(tilesAt, tiles.length),
    condenseGroupSettleEnd(tilesAt + MENU_BUTTON_TRAIL_S, menuPane.length),
    rowsAt + rows.tl.duration(),
  );
  master.call(
    () => revealLeaderboardAfterBeat(screen, leaderboard, furnitureSettled),
    [],
    furnitureSettledAt,
  );
  const secondaries = [...tiles, ...menuPane, ...rows.residue];
  master.eventCallback("onComplete", () => {
    entranceDone();
    if (!gsapDevMode()) shedEntranceResidue(screen, stamps, panel, secondaries);
  });
  return secondaries;
}

// Everything glass on the screen, condensing off the TILES beat: the reference strip's six tiles,
// and the start screen's own menu button trailing them by MENU_BUTTON_TRAIL_S. Two calls, not one
// staggered group — the button is top-right chrome, spatially discontiguous with the strip's
// left-to-right sweep, so a stagger that jumped mid-screen to the corner would read as a glitch
// rather than travel. The trail makes the screen read as one sweep that ends at the corner: the
// strip condenses through, then the menu breathes in last.
// `document.querySelector` for the button because it is a SIBLING of `screen` under #skat-app
// (renderGameStart mounts it unconditionally) — the same lookup the exit's flight into it makes.
//
// The two residues stay separate because each call feeds its own condenseGroupSettleEnd term in
// the furniture-settle Math.max above: the trail exceeds 5·staggerS (0.65s at defaults), past
// which the six-tile group's settle no longer dominates, so the pane carries its own term —
// tuning the trail in either direction can't silently outrun the leaderboard beat.
function addTilesBeatGlass(master, screen, at) {
  const tiles = screen.querySelectorAll(STRIP_TILES_SELECTOR);
  const menuButton = document.querySelector(".menu-button-start");
  return {
    tiles: addGlassCondense(master, tiles, at),
    menuPane: addGlassCondense(master, [menuButton], at + MENU_BUTTON_TRAIL_S),
  };
}

// Yield one frame, then report whether a Play click during it aborted this build —
// combines the chunk boundary's yield with its abort guard into one call. A Play
// click between yields runs finishEntranceEtch (via playGameStartExit), which
// kills the master, sheds whatever was staged so far, and nulls
// screen._entranceEtch — so a chunk resuming after the yield must not keep
// building onto the dead master (an unshed wrapEtchHost residue nothing would ever
// clean up). Every chunk but the first opens with this. On an abort it also
// resolves the completion promise, so a caller awaiting it doesn't hang.
async function yieldOrAbort(screen, master, resolveEntranceBuilt) {
  await nextFrame();
  if (screen._entranceEtch?.tl === master) return false;
  resolveEntranceBuilt();
  return true;
}

// The build's pre-chunk setup: stage the held-back leaderboard, compute the
// boot lead-in, record the partial in-flight etch (before any yield — see
// assembleEntranceMaster), and create the paused content sub-timeline chunks
// (a)-(c) build onto. Extracted so assembleEntranceMaster's own body stays
// readable as the five-chunk pipeline it is.
function beginEntranceBuild(master, screen) {
  const leaderboard = screen.querySelector(".game-start-leaderboard");
  if (leaderboard != null) gsap.set(leaderboard, { autoAlpha: 0 });
  const leadIn = bootLeadIn();
  screen._entranceEtch = { tl: master, stamps: [], panel: null, toggles: null };
  const content = gsap.timeline({ paused: true });
  return { leaderboard, leadIn, content };
}

// The Play plate is start-screen chrome: present + clickable from the first frame
// of the START screen (SCREENS Decision 3). But the lead-in is the LOADING screen
// (bar + blank beat), not the start screen — so on boot the plate is held hidden
// through it and becomes present the instant the start screen begins (leadIn),
// then its word + gate inscribe onto it. A return has no lead-in, so the plate
// stays present from frame 1 as before. Trusted non-null (buildPlayButton always
// emits it), as addGateEtch trusts its icon.
function holdPlayPlateThroughLoad(master, screen, leadIn) {
  if (leadIn <= 0) return;
  const playButton = screen.querySelector(".play-button");
  gsap.set(playButton, { autoAlpha: 0 });
  master.set(playButton, { autoAlpha: 1 }, leadIn);
}

// Release the leaderboard after a brief beat (LEADERBOARD_GAP_S), fired once the screen's
// furniture has settled (the positioned master.call() in attachContentAndWireCompletion) rather
// than waiting for the master's own true completion — the slow pip sweep and the tiles'
// lingering fog-clear tail are still allowed to run on underneath. Resolves `_furnitureSettled`
// (its row cascade waits on this — playLeaderboardReveal) and fades the held frame in together,
// both after the beat. `_entranceDone` is a SEPARATE promise (attachContentAndWireCompletion's
// own onComplete handler resolves it) — this beat is scoped to the leaderboard alone.
function revealLeaderboardAfterBeat(screen, leaderboard, furnitureSettled) {
  // Tracked on the screen so a Play click during the beat can cancel it
  // (finishEntranceEtch). Untracked, it would outlive the exit and fade the
  // leaderboard back in over the disassembly — the very thing the exit's
  // fadeOut prevents. Cleared once it fires.
  screen._leaderboardReveal = gsap.delayedCall(LEADERBOARD_GAP_S, () => {
    screen._leaderboardReveal = null;
    furnitureSettled();
    if (leaderboard != null)
      gsap.to(leaderboard, {
        autoAlpha: 1,
        duration: 0.35,
        ease: "power2.out",
      });
  });
}

// Hand every etched element back to the stylesheet and drop the in-flight handle:
// shed the logo/label/gate Stamp residue + the panel frame. Skipped under the
// scrubber, which needs the etch DOM to persist so it can replay (the shed would
// remove the very targets ?gsapdev wants to scrub); there the residue is left for
// the exit to shed (finishEntranceEtch), so it never orphans.
function shedEntranceResidue(screen, stamps, panel, toggleResidue) {
  shedEntranceEtch(stamps);
  shedPanelEtch(panel.residue);
  shedTogglesEtch(toggleResidue);
  screen._entranceEtch = null;
}

// The introduction is suppressed — reduced motion, or a return after it already
// played this page lifetime (SCREENS Decision 5 — re-introducing on every return
// cheapens the gesture). Resolve both chrome gates at once (`_entranceDone` for the idle
// warmups / e2e settle-guard, `_furnitureSettled` for the deferred leaderboard reveal —
// neither should wait on a suppressed entrance) and mark the visit so the row cascade rests
// rather than re-cascading on every Quit. On a motion-on return a quick fade-in smooths the
// board→start swap so it isn't an abrupt one-frame cut; reduced motion keeps the
// instant cut (a fade is a nicety, not load-bearing).
function bailEntranceWithoutIntro(screen, entranceDone, furnitureSettled) {
  screen._entrancePlayed = false;
  entranceDone();
  furnitureSettled();
  if (introHasPlayed && !prefersReducedMotion()) {
    gsap.fromTo(
      screen,
      { autoAlpha: 0 },
      { autoAlpha: 1, duration: RETURN_FADE_IN_S, ease: "power1.out" },
    );
  }
}

// Assemble the introduction onto `master` (created paused by the caller) across
// five chunks — (a)-(e), beginEntranceBuild's setup plus buildLogoChunk /
// buildLabelGateChunk / buildPanelNameChunk / attachContentAndWireCompletion above
// — each its own task via an awaited frame yield (yieldOrAbort), in place of the
// ~1.6s synchronous build this used to run in one task (tbt-reshaping slice 1 §2).
// beginEntranceBuild records the in-flight etch on the screen BEFORE the first
// yield, with an explicitly partial shape filled in as each chunk completes — so a
// Play click mid-build finds something to kill (playGameStartExit ->
// finishEntranceEtch, which null-guards the two atomic residues for exactly that
// partial shape). Resolves `resolveEntranceBuilt` after the final chunk's
// master.play() — or, on a chunk error, after shedding to factory presentation
// (the animation-error convention: a failed entrance must not break boot, so this
// never rejects `_entranceBuilt`). Residue is only published to screen._entranceEtch
// once a chunk function RETURNS (e.g. `screen._entranceEtch.stamps = logoStamps`,
// below) — a throw mid-chunk-body, after some but not all of that chunk's own DOM
// mutations (e.g. addLogoEtch's per-icon loop), sheds only what prior chunks already
// published, not the current chunk's partial work. Strictly better than pre-chunking
// behavior (any throw left every wrapped host permanently orphaned, no shed at all —
// renderGameStart's try/catch only warned); closing this narrower residual would need
// per-mutation tracking the design doesn't call for (see the chunk-error test).
async function assembleEntranceMaster(
  master,
  screen,
  entranceDone,
  furnitureSettled,
  resolveEntranceBuilt,
) {
  try {
    // Inside the try, not before it: beginEntranceBuild's own DOM read (the leaderboard
    // querySelector) and GSAP calls (gsap.set, gsap.timeline) are as capable of throwing
    // as any later chunk — a throw here left outside the try would escape as an
    // unhandled rejection AND leave `_entranceBuilt` permanently pending, hanging the
    // boot overlay forever (worse than any later chunk's failure, which at least
    // resolves and reveals a resting screen).
    const { leaderboard, leadIn, content } = beginEntranceBuild(master, screen);
    const logoStamps = buildLogoChunk(master, screen, leadIn);
    screen._entranceEtch.stamps = logoStamps;
    if (await yieldOrAbort(screen, master, resolveEntranceBuilt)) return;

    const { label, gate } = buildLabelGateChunk(content, screen);
    const stamps = [...logoStamps, ...(label ? [label] : []), gate];
    screen._entranceEtch.stamps = stamps;
    if (await yieldOrAbort(screen, master, resolveEntranceBuilt)) return;

    const panel = buildPanelNameChunk(content, screen);
    screen._entranceEtch.panel = panel.residue;
    if (await yieldOrAbort(screen, master, resolveEntranceBuilt)) return;

    screen._entranceEtch.toggles = attachContentAndWireCompletion(
      master,
      screen,
      content,
      { leadIn, leaderboard, stamps, panel, entranceDone, furnitureSettled },
    );
    if (await yieldOrAbort(screen, master, resolveEntranceBuilt)) return;

    // (e) Force every tween to init now, in one batch on a clean DOM — GSAP inits
    // lazily at first active tick, so each staggered etch stroke would otherwise
    // measure (DrawSVG getTotalLength/getBBox) and read computed style mid-
    // animation against a dirty DOM: 15-37 forced style recalcs per tick
    // (tbt-reshaping slice-1 probe). suppressEvents=true: onComplete (entranceDone, residue
    // shed) and the furniture-settled call (the leaderboard reveal) must not fire. The
    // read-heavy chunk, kept as its own task, right before play.
    master.progress(1, true).progress(0, true);
    master.play();
    resolveEntranceBuilt();
  } catch (e) {
    console.warn("[game-start-entrance]", e.message);
    finishEntranceEtch(screen);
    entranceDone();
    furnitureSettled();
    resolveEntranceBuilt();
  }
}

export function playGameStartEntrance(screen) {
  // Assigned as this function's first statement so a sync throw anywhere below
  // still leaves callers (renderGameStart, boot.js#renderStartScreen) a resolvable
  // promise rather than a missing property.
  let resolveEntranceBuilt;
  screen._entranceBuilt = new Promise(
    (resolve) => (resolveEntranceBuilt = resolve),
  );
  // Signal the rest of the screen when the introduction has FULLY played out — pips, fog
  // clear, everything — resolved in the master's true onComplete, or immediately on a bail
  // where no timeline runs. Three consumers outside this module hold on it: game-start-ui.js
  // defers its idle warmups (warmCardTemplates/warmGameGraph) past it (tbt-reshaping slice 1
  // §3, PERFORMANCE.md) and plays a reload's stashed identity-confirmation beat behind it (the
  // name slot is still invisible before then), and the initial-load-cls e2e guard waits on it
  // before reading its shift log. None wants the furniture-settled point below — all three need
  // the choreography genuinely finished, or their own "wait past the entrance" contract
  // silently weakens.
  let entranceDone;
  screen._entranceDone = new Promise((resolve) => (entranceDone = resolve));
  // The leaderboard's OWN gate — the furniture-settled re-key (SCREENS "furniture settled" /
  // this file's own header, start-screen-entrance-animations). A separate promise from
  // `_entranceDone` on purpose: the leaderboard wants to arrive well before the slow pip sweep
  // and the tiles' fog-clear tail finish, but nothing else does. Resolved by
  // revealLeaderboardAfterBeat (via attachContentAndWireCompletion's positioned master.call),
  // or immediately on a bail alongside `_entranceDone`.
  let furnitureSettled;
  screen._furnitureSettled = new Promise(
    (resolve) => (furnitureSettled = resolve),
  );
  // Bail before creating any tween when the introduction must not play.
  if (prefersReducedMotion() || introHasPlayed) {
    bailEntranceWithoutIntro(screen, entranceDone, furnitureSettled);
    resolveEntranceBuilt();
    return;
  }
  introHasPlayed = true;
  screen._entrancePlayed = true;

  // Paused: the build below spans several awaited frame yields (assembleEntranceMaster,
  // tbt-reshaping slice 1 §2). An unpaused master would start ticking — and rendering —
  // against a half-built timeline the instant a real animation frame elapses mid-build;
  // master.play() (the build's last chunk) is what actually starts it.
  const master = gsap.timeline({ paused: true });
  // Not awaited: this function keeps returning the master synchronously (its unit-test
  // and ?gsapdev scrubber contract) while the build runs in the background across
  // frame yields. Errors are contained inside (chunk-error containment, above) — this
  // can never leave an unhandled rejection.
  void assembleEntranceMaster(
    master,
    screen,
    entranceDone,
    furnitureSettled,
    resolveEntranceBuilt,
  );
  maybeAttachScrubber(master);
  return master;
}

// Finish an in-flight entrance: stop the etch and shed its residue synchronously,
// so the exit (or a re-entry) sees clean factory SVGs. No-op once the entrance
// completed and shed on its own. Always sheds — including the ?gsapdev residue
// the normal completion deliberately keeps.
function finishEntranceEtch(screen) {
  // Cancel a leaderboard reveal still waiting out its beat (Play clicked between
  // the intro settling and the leaderboard arriving) so it can't fade the
  // leaderboard back in over the disassembly. Independent of the etch below — the
  // etch may already have shed (the reveal is scheduled in the same onComplete).
  if (screen._leaderboardReveal != null) {
    screen._leaderboardReveal.kill();
    screen._leaderboardReveal = null;
  }
  const active = screen._entranceEtch;
  if (active == null) return;
  screen._entranceEtch = null;
  active.tl.kill();
  // stamps stays an always-iterable array (recorded [] before the first build
  // chunk), but panel/toggles are recorded null until their own chunk lands — a
  // Play click before chunk (c)/(d) must not throw shedding a residue that was
  // never built (the chunked build, tbt-reshaping slice 1 §2).
  shedEntranceEtch(active.stamps);
  if (active.panel != null) shedPanelEtch(active.panel);
  if (active.toggles != null) shedTogglesEtch(active.toggles);
}

// ── Exit: disassemble by fade, plus the strip's flight ───────────────────────

/**
 * Disassemble the game-start screen: everything on it fades out together on one timeline
 * (every fade added at position 0, all spanning ENTRANCE_DURATION_S) while the strip's tiles
 * fly into the menu button — their own departure gesture, the mirror of their glass-condense
 * arrival (flyStripIntoMenu).
 *
 * Nothing un-builds. Every group entered one-directionally (the etches, the condense) and the
 * name slot simply faded in, so each fade here is a `to()` from wherever the element currently
 * stands: the states the browser paints run rest → hidden, never a build flash. On a Play click
 * that lands mid-entrance, finishEntranceEtch below first restores every etched element to its
 * factory presentation, so only the name slot fades from a mid-flight pose.
 *
 * Returns a promise that resolves when the disassembly completes (or
 * immediately under reduced-motion), so the caller can start the session after.
 */
export function playGameStartExit(screen) {
  if (prefersReducedMotion()) return Promise.resolve();

  // Kill + shed any still-running entrance first, so the logo is clean factory
  // SVGs the fade below acts on — not host-wrapped nodes the deferred shed would
  // mutate mid-exit (a Play click landing before the inscription finished).
  finishEntranceEtch(screen);
  const tl = gsap.timeline();
  fadeOut(tl, screen, ".game-start-leaderboard", { y: 24 });
  // The name slot is the one cascade participant that faded in rather than inscribing (the
  // entrance's name reveal); it leaves with the same shape the rows group does, no reversal of
  // its own tween.
  fadeOut(tl, screen, ".game-start-name-slot", { y: 14 });
  // The Play button's entrance is the one-directional label etch, so fade the plate out here
  // or it lingers over the incoming game.
  fadeOut(tl, screen, ".play-button", { y: 14 });
  fadeOut(tl, screen, `${PIP_ICON_SELECTOR}, ${WORDMARK_SELECTOR}`, {
    scale: 0.6,
    transformOrigin: "center",
  });
  // The mode cards inscribed in — fade them out with a gentle scale, matching the exit
  // vocabulary.
  fadeOut(tl, screen, ".mode-card", {
    scale: 0.95,
    transformOrigin: "center",
  });
  // The rows group inscribes in too (a parallel cascade group, not a closing beat) —
  // EXIT_FADE_SELECTOR matches every toggle-group/segmented-picker/the Multiplayer bar, so
  // this one call fades the whole set out. The tiles group leaves by its own gesture, the
  // flight into the menu button (below).
  fadeOut(tl, screen, EXIT_FADE_SELECTOR, { y: 14 });
  // The Multiplayer section's expansion panel is never an entrance participant (it's plain
  // content, not build-vs-arriving — see SCREENS § "The Multiplayer section"), so it needs its
  // own fade here. This exit has two callers (Play — commitAndPlay — and a run-card click via
  // enterRun/playStartScreenExit): only the latter guarantees the panel is open with content,
  // but fading it unconditionally is still correct on the Play path too — a collapsed panel is
  // `max-height: 0; overflow: hidden`, so the tween has nothing visible to fade (review finding:
  // a prior version of this comment overclaimed the panel is always open here).
  fadeOut(tl, screen, ".mp-section-panel", { y: 14 });
  return Promise.all([tl.then(() => {}), flyStripIntoMenu(screen)]).then(
    () => {},
  );
}

// The strip's tiles fly into the top-right menu button (game-start-strip-flight.js) —
// the exit's one teaching beat: "this is where these live from now on". The button is
// the start screen's own `.menu-button-start` (a sibling of `screen` under #skat-app —
// game-start-ui.js#renderGameStart mounts it), never faded by this exit, so it's still
// standing when the in-game instance takes over at the same spot. The flight is the
// exit's last mover, so the disassembly's settle waits on it alongside the fades.
//
// `async` is load-bearing, not decoration: both callers wrap this exit in a
// catch-and-warn precisely so a failed animation can't block the game (the § Error
// handling convention in js/CLAUDE.md — commitAndPlay and enterRun's
// playStartScreenExit). This function is evaluated AFTER the fade timeline is already
// running, so a synchronous throw here would escape playGameStartExit before it ever
// returned a promise, leaving those catch handlers unattached and the player on a
// half-disassembled, pointer-inert screen with no session started. As a rejection it
// lands where the callers already handle it and the game still starts.
//
// No null guard on the target: renderGameStart mounts `.menu-button-start` and the strip
// unconditionally, so a missing one is a real bug, and a guard here would report it as a
// flight that ran (CODING § validate at boundaries / let errors propagate). Zero tiles
// needs no guard either — the flight simply weaves nothing.
async function flyStripIntoMenu(screen) {
  const target = document.querySelector(".menu-button-start");
  const tl = gsap.timeline();
  addStripFlight(tl, screen, target, screen, 0);
  return tl.then(() => {});
}

// Fade one selector's elements out onto the disassembly timeline at 0 — otherwise they linger
// visible over the incoming game, reading as a cut-off. The zero-target early return keeps an
// absent element from adding a do-nothing 0.5s tween to `tl`.
// play-test: the fades span the whole disassembly (0.5s) rather than clearing early at 0.7 of it
// — with the strip's flight as the exit's last mover (~1.1s), an earlier-emptied screen left
// the flock flying over a bare stage for most of its travel (Andre, 2026-08-30).
function fadeOut(tl, screen, selector, extra) {
  const targets = screen.querySelectorAll(selector);
  if (targets.length === 0) return;
  tl.to(
    targets,
    {
      autoAlpha: 0,
      duration: ENTRANCE_DURATION_S,
      ease: "power2.in",
      ...extra,
    },
    0,
  );
}

// ── Leaderboard reveal ───────────────────────────────────────────────────────

/**
 * Cascade the leaderboard rows in — each row fades + slides up in sequence, like
 * scores tallying. The leaderboard loads async and its data often lands DURING the
 * entrance, so the cascade is **deferred until the screen's furniture has settled**:
 * the leaderboard is secondary chrome and mustn't compete with the primary etch.
 *
 * The rows are staged hidden the instant they're built (so they don't sit
 * statically through the entrance), then cascaded once `_furnitureSettled` — the
 * screen-scoped promise playGameStartEntrance resolves at the furniture-settled point
 * (SCREENS "furniture settled" — well before the entrance's own true completion,
 * `_entranceDone`, which this module's other consumers wait on instead) — settles. If
 * that promise is absent (no entrance ran) or the reveal fell outside a .game-start
 * screen, the cascade fires immediately. Caller gates it to fire once per visit.
 */
export function playLeaderboardReveal(container) {
  if (prefersReducedMotion()) return;
  const screen = container.closest(".game-start");
  // The cascade is part of the introduction (it's gated on the entrance settling
  // below). On a return where the intro didn't replay (SCREENS Decision 5), the
  // entrance marked the screen _entrancePlayed = false — so the table appears at
  // rest with the rest of the screen rather than re-cascading on every Quit. A
  // context where no entrance ran at all (undefined) still cascades immediately.
  if (screen?._entrancePlayed === false) return;
  const rows = container.querySelectorAll(".leaderboard-list li");
  if (rows.length === 0) return;
  // Stage hidden now (a `from()` can't both pre-hide and read the visible end
  // state across the defer boundary — so set, then tween to the rest pose).
  gsap.set(rows, { autoAlpha: 0, y: 16 });
  // Returns the promise resolving to the cascade tween (once the gate settles) —
  // the caller ignores it; tests await it to seek the tween deterministically.
  return Promise.resolve(screen?._furnitureSettled).then(() =>
    gsap.to(rows, {
      autoAlpha: 1,
      y: 0,
      duration: 0.4,
      ease: "power3.out",
      stagger: 0.06, // play-test: rows tally in top-to-bottom.
    }),
  );
}

// ── Dev scrubber ─────────────────────────────────────────────────────────────

function gsapDevMode() {
  return new URLSearchParams(window.location.search).has("gsapdev");
}

function maybeAttachScrubber(tl) {
  // Opt-in scrubber for tuning the feel by hand: append ?gsapdev to the URL.
  // GSDevTools is a dev-only aid and must never ship to prod — it loads via a
  // dynamic CDN import inside this gate, so it never enters the production load
  // path and never runs in tests/CI. The .catch keeps a CDN failure to "no
  // scrubber on this dev load" (animation-error convention: warn and continue).
  if (gsapDevMode()) {
    import("https://cdn.jsdelivr.net/npm/gsap@3.15.0/GSDevTools.js/+esm")
      .then(({ GSDevTools }) => {
        gsap.registerPlugin(GSDevTools);
        GSDevTools.create({ animation: tl });
      })
      .catch((e) => console.warn("[gsapdev] scrubber unavailable:", e.message));
  }
}
